<template>
<l-map ref="map" :style="{height: innerHeight + 'px'}" :zoom="zoom" :center="center" :options="{zoomControl: false, bounceAtZoomLimits: false}" :minZoom="7" @update:bounds="boundsChanged" @update:zoom="zoomChanged" @ready="mapReady" @update:center="centerChanged" @click="handleMapClick">
    <l-tile-layer :url="url"></l-tile-layer>
    <l-marker :lat-lng="userCoords" v-if="userCoords != null" :options="{zIndexOffset: 601}">
        <l-icon :icon-size="dynamicSizePin" :icon-url="'/osm/new-me.png'" />
    </l-marker>
    <!--<v-marker-cluster :options="{spiderfyOnMaxZoom: false, showCoverageOnHover: false, zoomToBoundsOnClick: true, disableClusteringAtZoom: 13}">-->
        <l-marker
            :lat-lng="[poi.latitude,poi.longitude]"
            @click="poiMarkerClick(poi)"
            @touchstart="poiMarkerTouch(poi, $event)"
            v-for="poi in drawnPois"
            :key="'poi-' + poi.id"
            @mouseover="handleMarkerMouseOver(poi)"
        >
            <l-icon :icon-size="dynamicSize" :icon-anchor="dynamicAnchor" :icon-url="targets[poi.target_id]" />
            <l-tooltip v-if="!isMobile" :options="{direction: 'center'}"> {{ poiDetails == null ? '...' : poiDetails.name }}</l-tooltip>
        </l-marker>

    <!--</v-marker-cluster>-->

    <!-- Cluster Circles -->
     <l-marker :lat-lng="[cluster.geometry.coordinates[1], cluster.geometry.coordinates[0]]" v-for="cluster in drawnClusters" :key="'cluster-' + cluster.properties.id" :icon="cluster.properties.icon" @click="expandCluster(cluster)"/>

    <l-control-zoom v-if="controlsEnabled" position="bottomright"></l-control-zoom>
</l-map>
</template>

<script>
import Supercluster from 'supercluster';

import {
    LMap,
    LTileLayer,
    LMarker,
    LIcon,
    LControlZoom,
    LTooltip,
} from 'vue2-leaflet';

import L from 'leaflet';
import {
    apiCall,
    baseUri
} from '../utils/ApiMiddleware';
//import Vue2LeafletMarkerCluster from 'vue2-leaflet-markercluster'
import {
    GlobalEventEmitter
} from '../utils/GlobalEventEmitter';
export default {
    name: 'openStreetMap',
    props: {
        controlsEnabled: {
            type: Boolean,
            default: true
        },

        poiClick: {
            type: Function,
            default: function () {}
        },

        pois: {
            type: Array,
            default: function () {
                return [];
            }
        },

        center: {
            type: Array,
            default: function () {
                return [45.9488798, 8.5512232];
            }
        },

        userCoords: {
            type: Array,
            default: function () {
                return null;
            }
        },

        innerHeight: {
            type: Number,
            default: 100,
            required: false
        },

        bounds: {
            type: Array,
            default: null
        },

        isMobile: {
            type: Boolean,
            default: false
        }
    },
    components: {
        LMap,
        LTileLayer,
        LMarker,
        LIcon,
        LControlZoom,
        LTooltip,
        //'v-marker-cluster': Vue2LeafletMarkerCluster
    },

    data() {
        return {
            L,
            clusterIndex: null,

            zoom: 15,
            markerLatLng: [45.9488798, 8.5512232],
            poiDetails: null,
            dynamicSizePin: [50, 50],
            newZoom: null,
            baseUri,

            drawnPois: [],
            drawnClusters: [],

            userCentered: null,
            controlsObserver: null,
        }
    },

    methods: {
        handleMapClick(e) {
            // Only emit on mobile and if it's a simple tap (not drag/pan/zoom)
            // The event is from leaflet, check if there was no significant movement
            this.$emit('mapClick', e);
        },

        centerChanged(e) {
            //this.center = e;
            this.calcUserCenterDelta(e);
        },

        expandCluster(cluster){
            if(!this.$refs.map){
                return;
            }

            // If this cluster contains only one leaf, open it immediately (mobile-friendly, single tap)
            try {
                if (this.clusterIndex) {
                    const leaves = this.clusterIndex.getLeaves(cluster.properties.id, 2, 0); // fetch up to 2 to detect plurality
                    if (leaves && leaves.length === 1) {
                        const leaf = leaves[0];
                        if (!leaf.properties.cluster) {
                            const poi = {
                                id: leaf.properties.id,
                                latitude: leaf.geometry.coordinates[1],
                                longitude: leaf.geometry.coordinates[0],
                                target_id: leaf.properties.target_id
                            };
                            this.poiClick(poi);
                            return;
                        }
                    }
                }
            } catch (e) {
                // fallback to zoom behavior
            }

            // Otherwise, center it and zoom in one level
            const map = this.$refs.map.mapObject;
            const nextZoom = map.getZoom() + 1;
            map.setView(new L.LatLng(cluster.geometry.coordinates[1], cluster.geometry.coordinates[0]), nextZoom);
        },

        fitBoundsTo(b){
            if(!this.$refs.map || !b || b.length !== 2){
                return;
            }
            const sw = L.latLng(b[0][0], b[0][1]);
            const ne = L.latLng(b[1][0], b[1][1]);
            this.$refs.map.mapObject.fitBounds(L.latLngBounds(sw, ne), { padding: [10,10] });
        },

        boundsChanged(e) {
            this.drawElements(this.pois); // in this way we immediately recalculate clusters
            this.$emit('boundsChanged', e);
        },

        zoomChanged(e) {
            this.newZoom = e;
            this.dynamicSizePin = [50 * (e / 15), 50 * (e / 15)];
        },

        drawControls(){
            const container = this.$refs.map ? this.$refs.map.$el : null;
            const centerBtn = container ? container.querySelector('#center-user-btn') : null;
            const aiBtn = container ? container.querySelector('#ai-open-btn') : null;
            const aiContainer = container ? container.querySelector('#ai-control-container') : null;

            // If controls are disabled, remove custom controls if present and exit
            if (!this.controlsEnabled) {
                [centerBtn, aiBtn, aiContainer].forEach(b => {
                    if (b && b.parentNode) b.parentNode.removeChild(b);
                });
                return;
            }
            const target = container ? container.querySelector('.leaflet-control-zoom') : null;
            const root = container ? container.querySelector('.leaflet-bottom.leaflet-right') : null;
            if (!target || !root) {
                // Zoom controls not yet mounted or have been re-rendered; retry shortly
                setTimeout(() => { try { this.drawControls(); } catch(e){ console.warn(e); } }, 80);
                return;
            }

            // Ensure "center user" control exists (kept inside the zoom group)
            if (!centerBtn) {
                const el = document.createElement('a');
                el.id = 'center-user-btn';
                el.onclick = function(){
                    GlobalEventEmitter.$emit('askForGPS');
                }
                el.className = 'leaflet-control-zoom-in';
                el.style.display = 'flex';
                el.style.justifyContent = 'center';
                el.style.alignItems = 'center';
                const img = document.createElement('img');
                img.src = require('../assets/icons/center.png');
                img.alt = 'Center map';
                el.title = 'Center map';
                el.setAttribute('aria-label', 'Center map');
                el.appendChild(img);
                target.appendChild(el);
            }

            // Ensure "AI assistant" control exists as a separate floating control above the zoom group
            if (!aiContainer) {
                const ctl = document.createElement('div');
                ctl.id = 'ai-control-container';
                ctl.className = 'leaflet-control leaflet-control-ai';

                const ai = document.createElement('a');
                ai.id = 'ai-open-btn';
                ai.onclick = () => {
                    try { GlobalEventEmitter.$emit('openAIAssistant'); } catch(e){ console.warn(e); }
                };
                ai.className = 'leaflet-control-zoom-in';
                ai.style.display = 'flex';
                ai.style.justifyContent = 'center';
                ai.style.alignItems = 'center';

                const aiImg = document.createElement('img');
                aiImg.src = require('../assets/icons/ai.png');
                aiImg.alt = 'Assistente AI';
                ai.title = 'Assistente AI';
                ai.setAttribute('aria-label', 'Assistente AI');
                ai.appendChild(aiImg);

                ctl.appendChild(ai);

                // Insert above the zoom control so it appears "on top" in the stack
                if (target.parentNode === root) {
                    root.insertBefore(ctl, target);
                } else {
                    root.appendChild(ctl);
                }
            }
        },

        startControlsObserver(){
            try {
                if (this.controlsObserver) {
                    this.controlsObserver.disconnect();
                    this.controlsObserver = null;
                }
                const container = this.$refs.map ? this.$refs.map.$el : null;
                const corner = container ? container.querySelector('.leaflet-bottom.leaflet-right') : null;
                if (!corner) { 
                    // if corner not ready yet, drawControls will retry and we'll attach later
                    return;
                }
                this.controlsObserver = new MutationObserver(() => {
                    // if our controls are missing, attempt to recreate them
                    if (this.controlsEnabled) {
                        const centerBtn = container ? container.querySelector('#center-user-btn') : null;
                        const aiContainer = container ? container.querySelector('#ai-control-container') : null;
                        if (!centerBtn || !aiContainer) {
                            try { this.drawControls(); } catch(e){ console.warn(e); }
                        }
                    }
                });
                this.controlsObserver.observe(corner, { childList: true, subtree: true });
            } catch(e){ console.warn('controls observer error', e); }
        },

        stopControlsObserver(){
            try {
                if (this.controlsObserver) {
                    this.controlsObserver.disconnect();
                    this.controlsObserver = null;
                }
            } catch(e){ console.warn(e); }
        },

        mapReady() {
            this.drawControls();  
            this.startControlsObserver();

            this.drawElements(this.pois);

            if (this.bounds) {
                // wait a tick to ensure map sizes are computed
                this.$nextTick(() => {
                    this.fitBoundsTo(this.bounds);
                });
            }
        },

        async getPoi(poi) {
            this.poiDetails = null;
            const response = await apiCall('GET', '/pois/' + poi.id);
            if (response.status == 200) {
                this.poiDetails = response.data;
            } else if (response.status != 0) {
                this.$vs.notification({
                    title: 'Qualcosa è andato storto!',
                    text: 'Impossibile caricare il punto di interesse, riprova più tardi.',
                    color: 'danger',
                    position: 'top-right'
                })
            }
        },

        updateZoom(zoom) {
            this.newZoom = zoom;
        },

        invalidateMapSize(){
            if(!this.$refs.map){ return; }
            const map = this.$refs.map.mapObject;
            this.$nextTick(() => {
                setTimeout(() => {
                    try {
                        map.invalidateSize();
                        // do not recenter here; let caller decide (prevents overriding offset recenter)
                    } catch(e) {console.log(e);}
                }, 0);
            });
        },

        centerMapWithOffset(payload){
            if(!this.$refs.map || !payload){ return; }
            const map = this.$refs.map.mapObject;
            const lat = payload.lat != null ? payload.lat : (Array.isArray(payload) ? payload[0] : null);
            const lng = payload.lng != null ? payload.lng : (Array.isArray(payload) ? payload[1] : null);
            if(lat == null || lng == null){ return; }

            this.$nextTick(() => {
                setTimeout(() => {
                    try {
                        map.invalidateSize();
                        const size = map.getSize();
                        const offset = payload.offsetYpx != null ? payload.offsetYpx : Math.round(size.y * 0.18); // shift marker upward
                        const point = map.latLngToContainerPoint([lat, lng]);
                        const destPoint = this.L.point(point.x, point.y - offset);
                        const destLatLng = map.containerPointToLatLng(destPoint);
                        map.setView(destLatLng, this.newZoom != null ? this.newZoom : map.getZoom(), { animate: true });
                    } catch(e){console.log(e);}
                }, 50);
            });
        },

        poiMarkerClick(poi){
            // Always open immediately on click
            console.log('CLICKED POI', poi);
            this.poiClick(poi);
        },

        poiMarkerTouch(poi, ev){
            if (this.isMobile) {
                try {
                    // prevent Leaflet hover/tooltip focus behavior stealing first tap
                    if (ev && ev.preventDefault) ev.preventDefault();
                    if (ev && ev.stopPropagation) ev.stopPropagation();
                    // when Leaflet wraps event
                    if (ev && ev.originalEvent) {
                        if (ev.originalEvent.preventDefault) ev.originalEvent.preventDefault();
                        if (ev.originalEvent.stopPropagation) ev.originalEvent.stopPropagation();
                    }
                } catch(e){console.log(e);}
                this.poiClick(poi);
            }
        },

        handleMarkerMouseOver(poi){
            if(this.isMobile){
                return; // avoid first-tap hover on mobile causing double-tap UX
            }
            this.getPoi(poi);
        },

        drawElements(n){
            if(!this.$refs.map){
                return;
            }

            // Initialize Supercluster
            const index = new Supercluster({
                    log: false,
                    radius: 60,
                    extent: 256,
                    maxZoom: 17
                });

            // Create GeoJSON feature collection
            const geojson = {
                type: 'FeatureCollection',
                features: n.map(point => ({
                    type: 'Feature',
                    properties: { 
                        id: point.id,
                        target_id: point.target_id,
                    }, // Add your custom properties
                    geometry: {
                        type: 'Point',
                        coordinates: [point.longitude, point.latitude], // Longitude first!
                    }
                }))
            };

            console.log(geojson);

            // Load the points into the index
            index.load(geojson.features);
            this.clusterIndex = index;

            // Get current map bounds
            const bounds = this.$refs.map.mapObject.getBounds();

            // Convert Leaflet bounds to Supercluster bounding box
            let bbox = [
                bounds.getWest(),
                bounds.getSouth(),
                bounds.getEast(),
                bounds.getNorth(),
            ];

            // make bbox 4x bigger to load clusters outside the current viewport
            bbox = [
                bbox[0] - (bbox[2] - bbox[0]),
                bbox[1] - (bbox[3] - bbox[1]),
                bbox[2] + (bbox[2] - bbox[0]),
                bbox[3] + (bbox[3] - bbox[1]),
            ];

            // Get clusters for the current bounding box and zoom level
            const zoom = this.$refs.map.mapObject.getZoom();
            const clusters = index.getClusters(bbox, zoom);

            // Update the state
            this.drawnClusters = clusters.filter(cluster => cluster.properties.cluster).map(cluster => {
                const count = cluster.properties.point_count;
                const size =
                    count < 100 ? 'small' :
                    count < 1000 ? 'medium' : 'large';
                const icon = L.divIcon({
                    html: `<div><span>${cluster.properties.point_count_abbreviated}</span></div>`,
                    className: `marker-cluster marker-cluster-${size}`,
                    iconSize: L.point(40, 40)
                });

                return {
                    type: 'Feature',
                    properties: {
                        id: cluster.id,
                        icon: icon,
                    },
                    geometry: {
                        type: 'Point',
                        coordinates: [cluster.geometry.coordinates[0], cluster.geometry.coordinates[1]],
                    }
                }
            });
            this.drawnPois = clusters.filter(cluster => !cluster.properties.cluster).map(point => ({
                id: point.properties.id,
                latitude: point.geometry.coordinates[1],
                longitude: point.geometry.coordinates[0],
                target_id: point.properties.target_id,
            }));
        },

        async calcUserCenterDelta(e){
            if(!this.$refs.map){
                return;
            }

            // calc delta from user position to understand if user still almost centered and we follow it's movements
            if (this.userCoords != null) {
                const delta = Math.abs(this.userCoords[0] - e.lat) + Math.abs(this.userCoords[1] - e.lng);
                console.log('DELTA', delta);
                console.log('DELTA', this.$refs.map.mapObject.getZoom())
                // delta low and zoom not too far
                if (delta > 0.005 || this.$refs.map.mapObject.getZoom() < 14) {
                    this.userCentered = false;
                } else {
                    this.userCentered = true;
                }
            } else {
                this.userCentered = false;
            }
        }
    },

    created() {
        GlobalEventEmitter.$on('setZoom', this.updateZoom)
        GlobalEventEmitter.$on('invalidateMap', this.invalidateMapSize)
        GlobalEventEmitter.$on('centerWithOffset', this.centerMapWithOffset)
    },
    beforeDestroy() {
        GlobalEventEmitter.$off('setZoom', this.updateZoom)
        GlobalEventEmitter.$off('invalidateMap', this.invalidateMapSize)
        GlobalEventEmitter.$off('centerWithOffset', this.centerMapWithOffset)
        this.stopControlsObserver();
    },

    watch: {
        userCentered(n){
            console.log('USER STILL CENTERED', n);
            // change color of center button (guard when control not mounted or controls disabled)
            const container = this.$refs.map ? this.$refs.map.$el : null;
            const btn = container ? container.querySelector('#center-user-btn') : null;
            if (btn) {
                btn.style.backgroundColor = n ? '#ffad63' : 'white';
            }
            this.$emit('userCenteredChanged', n);
        },

        zoom(n) {
            console.log(n)
            this.dynamicSizePin = [50 * (n / 10), 50 * (n / 10)];
        },
        center(n) {
            if(!this.$refs.map){
                return;
            }
            this.$refs.map.mapObject.setView(new L.LatLng(n[0], n[1]), this.newZoom != null ? this.newZoom : this.zoom);
            if (this.newZoom != null) {
                this.zoom = this.newZoom;
                this.newZoom = null;
            }
        },

        pois: {
            handler(n) {
                this.drawElements(n);
            },
            immediate: true,
        },

        bounds(n) {
            this.fitBoundsTo(n);
        },

        controlsEnabled(n){
            this.drawControls();
            if (n) { this.startControlsObserver(); } else { this.stopControlsObserver(); }
        },

        innerHeight(){
            // when the map container height changes, invalidate and re-center
            this.invalidateMapSize();
        }

    },

    mounted() {

    },
    computed: {
        // CARTO requires an API key for its raster (PNG) basemaps; without it the tiles
        // show an "API key required" watermark. VUE_APP_CARTO_API_KEY is a public,
        // browser-exposed key baked in at build time (see .env) and restricted by
        // origin/domain on the CARTO side.
        url() {
            const base = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png';
            const key = process.env.VUE_APP_CARTO_API_KEY;
            return key ? `${base}?key=${encodeURIComponent(key)}` : base;
        },
        dynamicSize() {
            return [40.5, 46.5];
        },
        dynamicAnchor() {
            return [46.5 * 0.5, 0.86 * 40.5];
        },

        targets() {
            return this.$TARGETS.icons;
        }
    },
}
</script>

<style>
@import '~leaflet/dist/leaflet.css';
@import "~leaflet.markercluster/dist/MarkerCluster.css";
@import "~leaflet.markercluster/dist/MarkerCluster.Default.css";
</style><style>
@media (max-width: 600px) {
    .leaflet-bottom {
        bottom: 60px;
    }
}
.marker-cluster-small {
	background-color: rgba(181, 226, 140, 0.6);
	}
.marker-cluster-small div {
	background-color: rgba(110, 204, 57, 0.6);
	}

.marker-cluster-medium {
	background-color: rgba(241, 211, 87, 0.6);
	}
.marker-cluster-medium div {
	background-color: rgba(240, 194, 12, 0.6);
	}

.marker-cluster-large {
	background-color: rgba(253, 156, 115, 0.6);
	}
.marker-cluster-large div {
	background-color: rgba(241, 128, 23, 0.6);
	}

.marker-cluster {
	background-clip: padding-box;
	border-radius: 20px;
	}
.marker-cluster div {
	width: 30px;
	height: 30px;
	margin-left: 5px;
	margin-top: 5px;

	text-align: center;
	border-radius: 15px;
	font: 12px "Helvetica Neue", Arial, Helvetica, sans-serif;
	}
.marker-cluster span {
	line-height: 30px;
	}
</style>
<style>
/* Modern glass controls grouped on same background */
.leaflet-control-zoom,
.leaflet-control-ai {
  border: 1px solid rgba(255, 255, 255, 0.35) !important;
  background: rgba(255, 255, 255, 0.55) !important;
  -webkit-backdrop-filter: saturate(150%) blur(10px);
  backdrop-filter: saturate(150%) blur(10px);
  border-radius: 16px !important;
  padding: 4px !important;
  box-shadow: 0 8px 20px rgba(16, 24, 40, 0.12);
}

/* Zoom +/- and custom center button (embedded inside the grouped container) */
.leaflet-control-zoom a,
.leaflet-control-ai a,
#center-user-btn {
  width: 36px !important;
  height: 36px !important;
  border-radius: 10px !important;
  border: none !important;
  background: transparent !important;
  color: #27303f !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  box-shadow: none !important;
  margin: 3px 0 !important;
  padding: 0 !important;
}

/* Hover state: subtle highlight inside the grouped container */
.leaflet-control-zoom a:hover,
.leaflet-control-ai a:hover,
#center-user-btn:hover {
  background: rgba(255, 255, 255, 0.75) !important;
  border-radius: 12px !important;
}

/* Improve +/- glyph legibility */
.leaflet-control-zoom-in,
.leaflet-control-zoom-out {
  font-size: 18px !important;
  line-height: 1 !important;
  font-weight: 700;
}

/* Center button icon */
#center-user-btn img,
#ai-open-btn img {
  width: 18px !important;
  height: 18px !important;
  margin: 0 !important;
  filter: none;
}

/* Mobile sizes */
@media (max-width: 600px) {
  .leaflet-control-zoom a,
  .leaflet-control-ai a,
  #center-user-btn {
    width: 40px !important;
    height: 40px !important;
  }
}

/* Micro-interactions for grouped map controls */
.leaflet-control-zoom,
.leaflet-control-ai {
  transition: transform .18s ease, box-shadow .25s ease, backdrop-filter .25s ease, background-color .25s ease, border-color .25s ease;
  animation: osm-float-in .24s ease both;
}
.leaflet-control-zoom a,
.leaflet-control-ai a,
#center-user-btn {
  transition: transform .12s ease, background-color .2s ease, filter .2s ease;
  will-change: transform;
}
.leaflet-control-zoom a:hover,
.leaflet-control-ai a:hover,
#center-user-btn:hover {
  /* keep existing lightening and add slight scale */
  transform: translateZ(0) scale(0.98);
}
.leaflet-control-zoom a:active,
.leaflet-control-ai a:active,
#center-user-btn:active {
  transform: scale(.95);
}

@keyframes osm-float-in {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* Lift Leaflet controls above native (Capacitor) bottom nav with safe-area on mobile only */
@media (max-width: 600px) {
  .tourial-app.tourial-native .leaflet-bottom {
    bottom: max(60px, calc(60px + env(safe-area-inset-bottom))) !important;
  }
}

#ai-control-container {
    width: 48px!important;
    height: 48px!important;
    border-radius: 100px!important;
}

#ai-open-btn {
    width: 100%!important;
    height: 100%!important;
}

#ai-open-btn img {
    width: auto!important;
    margin: auto!important;
    height: 28px!important;
}
</style>
